import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Stepper, react, TextBox, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import MaibPaymentsCurrency from "./components/MaibPaymentsCurrency";
import MaibPaymentsCountryIso from "./components/MaibPaymentsCountryIso";

const MaibPaymentBase = react.withController<
	MaibPayment.PropsBase,
	MaibPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<MaibPaymentsCurrency.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<MaibPaymentsCountryIso.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	// maibPaymentsAdditionalFee
	// const maibPaymentsAdditionalFeeValue = valueEditor.useGetter(
	// 	"maibPaymentsAdditionalFee",
	// );
	// const setMaibPaymentsAdditionalFeeValue = valueEditor.useSetter(
	// 	"maibPaymentsAdditionalFee",
	// );
	// const handleMaibPaymentsAdditionalFee = useCallback(
	// 	(value: Stepper.Value) => {
	// 		if (typeof value === "number") {
	// 			setMaibPaymentsAdditionalFeeValue(value);
	// 		}
	// 	},
	// 	[setMaibPaymentsAdditionalFeeValue],
	// );
	// const textMaibPaymentsAdditionalFee = useMemo(
	// 	() =>
	// 		t([
	// 			`${translationPath}.modal.text.maibPaymentsAdditionalFee`,
	// 			"Commission in UAH.",
	// 		]),
	// 	[t],
	// );

	// maibPaymentsFeePercentage
	// const maibPaymentsFeePercentageValue = valueEditor.useGetter(
	// 	"maibPaymentsFeePercentage",
	// );
	// const setMaibPaymentsFeePercentageValue = valueEditor.useSetter(
	// 	"maibPaymentsFeePercentage",
	// );
	// const handleMaibPaymentsFeePercentage = useCallback(
	// 	(value: Stepper.Value) => {
	// 		if (typeof value === "number") {
	// 			setMaibPaymentsFeePercentageValue(value);
	// 		}
	// 	},
	// 	[setMaibPaymentsFeePercentageValue],
	// );

	// const textMaibPaymentsFeePercentage = useMemo(
	// 	() =>
	// 		t([
	// 			`${translationPath}.modal.text.maibPaymentsFeePercentage`,
	// 			"Commission in %",
	// 		]),
	// 	[t],
	// );

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	// textMaibPaymentsAdditionalFee
	// textMaibPaymentsFeePercentage
	// textSystemFeeAdditionalForExecutor
	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);
	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// maibPaymentsKey
	const maibPaymentsKey = valueEditor.useGetter("maibPaymentsKey");
	const setMaibPaymentsKey = valueEditor.useSetter("maibPaymentsKey");
	const [errorMaibPaymentsKey, setMaibPaymentsKeyError] =
		useState<boolean>(false);
	const handleMaibPaymentsKey = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setMaibPaymentsKey(value);
				setMaibPaymentsKeyError(false);
			}
		},
		[setMaibPaymentsKey, setMaibPaymentsKeyError],
	);

	const textErrorMaibPaymentsKey = useMemo(() => {
		if (!errorMaibPaymentsKey) return "";
		return t([
			`${translationPath}.modal.errors.paymentsKey`,
			"Key not entered",
		]);
	}, [t, errorMaibPaymentsKey]);

	const placeholderMaibPaymentsKey = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsKey`,
				"Enter key",
			]),
		[t],
	);

	const textMaibPaymentsKey = useMemo(
		() => t([`${translationPath}.modal.text.paymentsKey`, "Key"]),
		[t],
	);

	const textMaibSignatureId = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.maibSignatureId`,
				"Processing number",
			]),
		[t],
	);

	// maibPaymentsSecretKey
	const maibPaymentsSecretKey = valueEditor.useGetter(
		"maibPaymentsSecretKey",
	);
	const maibPaymentsSecretKeySet = valueEditor.useSetter(
		"maibPaymentsSecretKey",
	);
	const [errorMaibPaymentsSecretKey, setMaibPaymentsSecretKeyError] =
		useState<boolean>(false);
	const maibPaymentsSecretKeyHandle = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				maibPaymentsSecretKeySet(value);
				setMaibPaymentsSecretKeyError(false);
			}
		},
		[maibPaymentsSecretKeySet, setMaibPaymentsSecretKeyError],
	);

	const textErrorMaibPaymentsSecretKey = useMemo(() => {
		if (!errorMaibPaymentsSecretKey) return "";
		return t([
			`${translationPath}.modal.errors.paymentsSecretKey`,
			"Secret key not entered",
		]);
	}, [t, errorMaibPaymentsSecretKey]);

	const placeholderMaibPaymentsSecretKey = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsSecretKey2`,
				"Enter the secret key",
			]),
		[t],
	);
	const textMaibPaymentsSecretKey = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.paymentsSecretKey`,
				"The secret key",
			]),
		[t],
	);

	// maibSignatureId // maibSignatureId  MaibSignatureId
	const maibSignatureId = valueEditor.useGetter("maibSignatureId");
	const setMaibSignatureId = valueEditor.useSetter("maibSignatureId");
	const [errorMaibSignatureId, setMaibSignatureIdError] =
		useState<boolean>(false);
	const handleMaibSignatureId = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setMaibSignatureId(value);
				setMaibSignatureIdError(false);
			}
		},
		[setMaibSignatureId],
	);

	const textErrorMaibSignatureId = useMemo(() => {
		if (!errorMaibSignatureId) return "";
		return t([
			`${translationPath}.modal.errors.maibSignatureId`,
			"Processing number not entered",
		]);
	}, [t, errorMaibSignatureId]);

	const placeholderMaibSignatureId = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.maibSignatureId`,
				"Enter processing number",
			]),
		[t],
	);

	// maibPaymentsProcessingId
	// const maibPaymentsProcessingId = valueEditor.useGetter(
	// 	"maibPaymentsProcessingId",
	// );
	// const maibPaymentsProcessingIdSet = valueEditor.useSetter(
	// 	"maibPaymentsProcessingId",
	// );
	// const [errorMaibProcessingId, setMaibProcessingIdError] =
	// 	useState<boolean>(false);
	// const maibPaymentsProcessingIdHandle = useCallback(
	// 	(value: string) => {
	// 		if (typeof value === "string") {
	// 			maibPaymentsProcessingIdSet(Number(value));
	// 			setMaibProcessingIdError(false);
	// 		}
	// 	},
	// 	[maibPaymentsProcessingIdSet, setMaibProcessingIdError],
	// );

	// const textErrorMaibProcessingId = useMemo(() => {
	// 	if (!errorMaibProcessingId) return "";
	// 	return t([
	// 		`${translationPath}.modal.errors.paymentsProcessingId`,
	// 		"Processing number not entered",
	// 	]);
	// }, [t, errorMaibProcessingId]);

	// const placeholderMaibProcessingId = useMemo(
	// 	() =>
	// 		t([
	// 			`${translationPath}.modal.placeholders.paymentsProcessingId`,
	// 			"Enter processing number",
	// 		]),
	// 	[t],
	// );
	// const textMaibProcessingId = useMemo(
	// 	() =>
	// 		t([
	// 			// `${translationPath}.modal.text.paymentsProcessingId`,
	// 			"Processing number",
	// 		]),
	// 	[t],
	// );
	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);
	// controller
	controller.setContext({
		// maibPaymentsProcessingId,
		maibPaymentsSecretKey,
		maibPaymentsKey,
		maibSignatureId,
		currencyRef,
		countryIsoRef,
		setMaibPaymentsSecretKeyError,
		// setMaibProcessingIdError,
		setMaibPaymentsKeyError,
		setMaibSignatureIdError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibPaymentsKey} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderMaibPaymentsKey}
					error={textErrorMaibPaymentsKey}
					value={maibPaymentsKey}
					onChange={handleMaibPaymentsKey}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibPaymentsSecretKey} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					error={textErrorMaibPaymentsSecretKey}
					placeholder={placeholderMaibPaymentsSecretKey}
					value={maibPaymentsSecretKey}
					onChange={maibPaymentsSecretKeyHandle}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibSignatureId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					error={textErrorMaibSignatureId}
					placeholder={placeholderMaibSignatureId}
					value={maibSignatureId}
					onChange={handleMaibSignatureId}
				/>
			</StyledRow>

			{/* <StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibProcessingId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "300px" }}
					error={textErrorMaibProcessingId}
					placeholder={placeholderMaibProcessingId}
					type="number"
					min={0}
					value={String(maibPaymentsProcessingId)}
					onChange={maibPaymentsProcessingIdHandle}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibPaymentsFeePercentage}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={maibPaymentsFeePercentageValue}
					onChange={handleMaibPaymentsFeePercentage}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textMaibPaymentsAdditionalFee}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={maibPaymentsAdditionalFeeValue}
					onChange={handleMaibPaymentsAdditionalFee}
				/>
			</StyledRow> */}

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<MaibPaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<MaibPaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const MaibPayment = memo(MaibPaymentBase);

declare namespace MaibPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default MaibPayment;
